// Copy + treatment for the AI Interview "interview not completed" banner,
// driven by how the interview actually ended (`completionReason`).
//
// The banner used to say "The candidate ended this interview before answering
// every question" for EVERY early ending, including one where the system
// stopped the interview because its time limit was reached. That reads as a
// mark against a candidate who did nothing wrong, and it contradicted the
// executive summary sitting directly below it.
//
// Two endings, two treatments:
//   - The candidate stopped (chose to end, refused to continue, walked away,
//     lost the interview to the proctor): amber. A recruiter should weigh it.
//   - The system stopped it (time limit, question limit): neutral slate. It is
//     context for reading the score, not a caution about the person. Amber here
//     tells a recruiter "be wary" about something the candidate never chose.
//
// Reason vocabulary is owned by student-node
// (app/helpers/aiInterviewCompletionReason.js). Keep the two in sync; an
// unrecognised or missing reason falls back to the candidate-ended wording,
// which is exactly how every session recorded before 2026-08-06 was stored.
//
// See aiInterviewEnding.test.js.

// Endings where nothing about the missing questions is the candidate's doing.
const SYSTEM_ENDED_REASONS = ['all_parameters_covered', 'question_cap_reached', 'time_up']

const ENDINGS = {
  all_parameters_covered: {
    label: 'Interview Ended Early',
    headline: 'The interview covered every evaluation parameter and closed on its own.',
  },
  candidate_initiated: {
    label: 'Interview Not Completed',
    headline: 'The candidate ended this interview before answering every question.',
  },
  candidate_unwilling: {
    label: 'Interview Not Completed',
    headline: 'The candidate declined to continue answering, so the interview was closed.',
  },
  dropout: {
    label: 'Interview Not Completed',
    headline: 'The candidate did not return to finish this interview. It was closed automatically.',
  },
  proctoring_violation: {
    label: 'Interview Ended By Proctoring',
    headline: 'The interview was closed automatically after repeated full-screen exits.',
  },
  question_cap_reached: {
    label: 'Question Limit Reached',
    headline: 'The interview reached its configured question limit and closed on its own.',
  },
  time_up: {
    label: 'Time Limit Reached',
    headline: "The interview's time limit was reached before every question could be asked.",
  },
}

const FALLBACK_REASON = 'candidate_initiated'

// Neutral slate for a system ending, amber for a candidate one. Text colors are
// the ink end of each ramp so body copy clears 4.5:1 on its own tint.
const TONES = {
  informational: {
    background: '#f8fafc',
    border: '#e2e8f0',
    label: '#475569',
  },
  caution: {
    background: '#fffbeb',
    border: '#fde68a',
    label: '#b45309',
  },
}

/**
 * @param {string|null|undefined} completionReason How the interview ended.
 * @return {{label: string, headline: string, isCandidateEnded: boolean, tone: object}}
 *   Everything the banner needs to render. Never null.
 */
const describeInterviewEnding = (completionReason) => {
  const reason = ENDINGS[completionReason] ? completionReason : FALLBACK_REASON
  const isCandidateEnded = !SYSTEM_ENDED_REASONS.includes(reason)
  return {
    ...ENDINGS[reason],
    isCandidateEnded,
    tone: isCandidateEnded ? TONES.caution : TONES.informational,
  }
}

/**
 * The line under the headline: how much was covered, and how to read the score.
 *
 * @param {object} args
 * @param {number} args.answered Answered turns.
 * @param {number|string} args.expected Turns the interview budgeted.
 * @param {boolean} args.hasScore Whether a score exists.
 * @param {boolean} args.isCandidateEnded From describeInterviewEnding.
 * @param {boolean} args.isBelowScoringBar Too little answered to ever be graded.
 * @return {string}
 */
const describeScoreImpact = ({
  answered,
  expected,
  hasScore,
  isCandidateEnded,
  isBelowScoringBar,
}) => {
  const coverage = `${answered} of ${expected} questions were answered.`
  if (hasScore) {
    // Worth stating outright on a system ending: the score is NOT scaled down
    // for the unasked questions (fastapi-ai-engine skips the completion penalty
    // unless the candidate chose to stop), and a recruiter reading "6 of 8"
    // will otherwise assume it was.
    return isCandidateEnded
      ? `${coverage} The score below was generated from that partial transcript, so read it alongside the questions attempted.`
      : `${coverage} The score below covers only what was asked and carries no penalty for the questions that were never reached.`
  }
  if (answered === 0) {
    return `${coverage} No answers were captured, so no fit score could be generated.`
  }
  if (isBelowScoringBar) {
    return `${coverage} That is too little of the interview to grade fairly, so no fit score was generated. The questions attempted are shown below.`
  }
  return `${coverage} A fit score for this partial transcript is still being generated, so check back shortly.`
}

module.exports = {
  ENDINGS,
  SYSTEM_ENDED_REASONS,
  describeInterviewEnding,
  describeScoreImpact,
}
